import { observable, action, makeObservable } from 'mobx'

import type { ShopRootStore } from 'shop/stores/shopRoot.store'
import {
  createEventAttendeesV2Api,
  eventAttendeesFailed,
  EventAttendeesV2Api,
  EventAttendeeSeatV2,
  EventAttendeeDetailV2,
  EventAttendeeListFilters,
  EventAttendeeBulkUpdateItem,
  EventAttendeeBulkUpdateResponse,
  EventAttendeeListResponse,
  TicketTypeListResponse,
  TicketTypeOptionV2,
} from 'shop/api/eventAttendeesV2.api'

const DEFAULT_PAGE = 1
const DEFAULT_PER = 10

// JSON:API ids are strings on the wire; the rest of the manage-attendees UI (sorting, Sets of
// selected rows) works with numbers, same as the legacy v1 payload. Convert once, at the boundary.
export interface EventAttendeeRowV2 extends Omit<EventAttendeeSeatV2, 'id'> {
  id: number
  ticketAttendee: (Omit<EventAttendeeDetailV2, 'id'> & { id: number }) | null
}

// `ApiClientV2`'s response pipeline flattens `data[]` items (id + attributes at the top level,
// `relationships` left intact) but does not touch `included[]` — those stay raw JSON:API
// resource objects (`{id, type, attributes}`). Resolve the sideloaded attendee row here.
const resolveSeats = (data: Record<string, any>[] = [], included: Record<string, any>[] = []): EventAttendeeRowV2[] => {
  const includedMap = new Map(included.map((item) => [`${item.type}:${item.id}`, item]))

  return data.map((seat) => {
    const pointer = seat.relationships?.ticketAttendee?.data
    const detailRaw = pointer ? includedMap.get(`${pointer.type}:${pointer.id}`) : undefined
    const ticketAttendee = detailRaw ? { ...detailRaw.attributes, id: Number(detailRaw.id) } : null

    const { relationships, id, ...seatAttrs } = seat
    return { ...seatAttrs, id: Number(id), ticketAttendee } as EventAttendeeRowV2
  })
}

export class EventAttendeesV2Store {
  storeName = 'EventAttendeesV2Store'
  api: EventAttendeesV2Api
  private root: ShopRootStore

  constructor(root: ShopRootStore) {
    this.root = root
    this.api = createEventAttendeesV2Api(root.apiClientV2)
    makeObservable(this)
  }

  @observable list: EventAttendeeRowV2[] = []
  @observable loading = false
  @observable pagination = { page: DEFAULT_PAGE, per: DEFAULT_PER, total: 0 }
  // Server-side filters only — ticket_id + personalisation_status (the "assignment status"
  // filter, named after the badge it matches) + query. No client-side filtering.
  @observable filters: EventAttendeeListFilters = {}
  @observable ticketTypeOptions: TicketTypeOptionV2[] = []

  username = ''
  orderToken = ''

  @action.bound
  setUrlParamsData = ({ token, username }: { token: string; username: string }) => {
    this.orderToken = token
    this.username = username
  }

  @action.bound
  setLoading = (value: boolean) => {
    this.loading = value
  }

  @action.bound
  setList = (list: EventAttendeeRowV2[]) => {
    this.list = list
  }

  @action.bound
  setPagination = (pagination: Partial<{ page: number; per: number; total: number }>) => {
    this.pagination = { ...this.pagination, ...pagination }
  }

  @action.bound
  setTicketTypeOptions = (options: TicketTypeOptionV2[]) => {
    this.ticketTypeOptions = options
  }

  @action.bound
  async fetchTicketTypes(): Promise<TicketTypeListResponse> {
    const resp = await this.api.fetchTicketTypes(this.username, this.orderToken)

    if (!eventAttendeesFailed(resp)) {
      this.setTicketTypeOptions(
        (resp.data ?? [])
          .map((ticketType) => ({ value: Number(ticketType.id), label: ticketType.name ?? '' }))
          .filter((option) => Number.isFinite(option.value))
      )
    }

    return resp
  }

  @action.bound
  resetScopes = () => {
    this.filters = {}
  }

  @action.bound
  handleScopeChange = <K extends keyof EventAttendeeListFilters>(key: K, value: EventAttendeeListFilters[K]) => {
    this.filters = { ...this.filters, [key]: value }
  }

  @action.bound
  setQuery = (value: string) => {
    const trimmed = value?.trim()
    if (trimmed) {
      this.filters = { ...this.filters, query: trimmed }
    } else {
      const { query, ...rest } = this.filters
      this.filters = rest
    }
  }

  @action.bound
  async fetchList(): Promise<EventAttendeeListResponse> {
    this.setLoading(true)

    const resp = await this.api.fetchList(this.username, this.orderToken, {
      filter: this.filters,
      page: { number: this.pagination.page, size: this.pagination.per },
    })

    if (!eventAttendeesFailed(resp)) {
      this.setList(resolveSeats(resp.data, resp.included))
      this.setPagination({ total: resp.totalCount ?? 0 })
    }

    this.setLoading(false)
    return resp
  }

  // Mirrors SharedStore's applyFilters: reset to page 1, then refetch under the new filters.
  @action.bound
  applyFilters() {
    this.setPagination({ page: DEFAULT_PAGE })
    return this.fetchList()
  }

  @action.bound
  handlePaginationChange(key: 'page' | 'per', value: number) {
    this.setPagination(key === 'per' ? { per: value, page: DEFAULT_PAGE } : { page: value })
    return this.fetchList()
  }

  // One transaction: a refusal on any row rolls the whole batch back and writes none. Merges the
  // written rows back into the current list from the response — no refetch needed on success.
  @action.bound
  async bulkUpdate(items: EventAttendeeBulkUpdateItem[], recaptcha?: string): Promise<EventAttendeeBulkUpdateResponse> {
    const resp = await this.api.bulkUpdate(this.username, this.orderToken, {
      data: items,
      meta: { ...(recaptcha ? { recaptcha } : {}) },
    })

    if (!eventAttendeesFailed(resp)) {
      const updated = resolveSeats(resp.data, resp.included)
      const byId = new Map(updated.map((row) => [row.id, row]))
      this.setList(this.list.map((row) => byId.get(row.id) ?? row))
    }

    return resp
  }
}
